// Einstellungen → Regeln (Phase 2.C): Liste mit Schalter, Editor für Bedingungen/Aktionen,
// „Regel in normalen Worten“ (Bob baut den Vorschlag), Probelauf über 30 Tage, Protokoll mit
// Rückgängig und die Freigabeliste für Weiterleitungen (nur Admins).
import { useCallback, useEffect, useState } from 'react';
import {
  ADMIN_AKTIONEN,
  AKTION_LABEL,
  BEDINGUNG_LABEL,
  TEAM,
  aktionText,
  bedingungText,
  type Aktion,
  type Bedingung,
  type Regel,
} from '@mail/shared';
import {
  api,
  type Probelauf,
  type ProtokollEintrag,
  type RegelRow,
  type RegelnResponse,
} from '../lib/api.ts';
import { fmtDateTime as formatDate } from '../lib/format.ts';
import { toast } from './Toast.tsx';

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const PRESETS: Record<string, string> = {
  today16: 'heute 16:00',
  tomorrow: 'morgen früh',
  monday: 'Montag',
  nextweek: 'in einer Woche',
};

function neueBedingung(art: Bedingung['art']): Bedingung {
  switch (art) {
    case 'absender_adresse':
    case 'absender_domain':
    case 'postfach':
    case 'betreff_enthaelt':
    case 'text_enthaelt':
      return { art, wert: '' };
    case 'anhang':
      return { art, typ: 'pdf' };
    case 'wochentag':
      return { art, tage: [1, 2, 3, 4, 5] };
    case 'uhrzeit':
      return { art, von: '08:00', bis: '17:00' };
    case 'bob_erkennt':
      return { art, text: '', minSicherheit: 0.8 };
    default:
      return { art };
  }
}
function neueAktion(typ: Aktion['typ'], allow: string[]): Aktion {
  switch (typ) {
    case 'easybill':
      return { typ, bestaetigung: true };
    case 'weiterleiten':
      return { typ, an: allow[0] ?? '', modus: 'weiterleitung' };
    case 'antworten':
      return { typ, text: '' };
    case 'zuweisen':
      return { typ, an: 'till' };
    case 'einsortieren':
      return { typ, kategorie: 'info' };
    case 'spaeter':
      return { typ, preset: 'tomorrow' };
    case 'crm_notiz':
      return { typ, text: '' };
    case 'crm_erinnerung':
      return { typ, titel: '', tage: 1 };
    case 'bob_entwurf':
      return { typ };
    default:
      return { typ };
  }
}

const leer = (): Regel => ({
  name: '',
  aktiv: false,
  reihenfolge: 100,
  bedingungen: { alle: [], eine: [] },
  aktionen: [],
  beschreibung: null,
});

export function RegelnSettings() {
  const [data, setData] = useState<RegelnResponse | null>(null);
  const [edit, setEdit] = useState<Regel | null>(null);
  const [tab, setTab] = useState<'regeln' | 'protokoll' | 'freigabe'>('regeln');
  const reload = useCallback(
    () =>
      api
        .regeln()
        .then(setData)
        .catch((e: Error) => toast(e.message)),
    [],
  );
  useEffect(() => void reload(), [reload]);
  if (!data) return <div className="muted">Regeln werden geladen …</div>;
  const allow = data.freigabeliste.map((f) => f.address);

  const toggle = async (r: RegelRow) => {
    try {
      await api.regelSave({ ...r, aktiv: !r.aktiv });
      await reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const remove = async (r: RegelRow) => {
    if (!confirm(`Regel „${r.name}“ löschen?`)) return;
    await api.regelDelete(r.id);
    await reload();
  };

  return (
    <div className="rg">
      <div className="rg-tabs">
        {(['regeln', 'protokoll', 'freigabe'] as const).map((t) => (
          <button key={t} className={`chip${tab === t ? ' on' : ''}`} onClick={() => setTab(t)}>
            {{ regeln: 'Regeln', protokoll: 'Zuletzt ausgeführt', freigabe: 'Freigabeliste' }[t]}
          </button>
        ))}
      </div>
      {tab === 'regeln' && !edit && (
        <>
          {data.regeln.length === 0 && (
            <div className="muted">
              Noch keine Regeln. Beispiel: „Staff-Rechnungen an easybill“.
            </div>
          )}
          {data.regeln.map((r) => (
            <div key={r.id} className="rg-row">
              <label className="rg-switch" title={r.aktiv ? 'aktiv' : 'pausiert'}>
                <input type="checkbox" checked={r.aktiv} onChange={() => void toggle(r)} />
                <span />
              </label>
              <div className="rg-main" onClick={() => setEdit(r)} role="button" tabIndex={0}>
                <div className="rg-name">
                  {r.name} {!r.aktiv && <span className="pill mut">pausiert</span>}
                </div>
                <div className="rg-text">
                  Wenn{' '}
                  {[
                    ...r.bedingungen.alle.map(bedingungText),
                    ...(r.bedingungen.eine.length
                      ? ['(' + r.bedingungen.eine.map(bedingungText).join(' oder ') + ')']
                      : []),
                  ].join(' und ')}{' '}
                  → {r.aktionen.map(aktionText).join(', ')}
                </div>
                <div className="muted" style={{ fontSize: 11.5 }}>
                  geändert {formatDate(r.updatedAt)} von {r.geaendertVon ?? '–'}
                </div>
              </div>
              <button className="btn sm ghost" onClick={() => void remove(r)} title="Löschen">
                ✕
              </button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button className="btn sm pri" onClick={() => setEdit(leer())}>
              + Regel
            </button>
          </div>
        </>
      )}
      {tab === 'regeln' && edit && (
        <RegelEditor
          regel={edit}
          allow={allow}
          admin={data.admin}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            void reload();
          }}
        />
      )}
      {tab === 'protokoll' && <Protokoll />}
      {tab === 'freigabe' && <Freigabeliste data={data} reload={reload} />}
    </div>
  );
}

function RegelEditor({
  regel,
  allow,
  admin,
  onClose,
  onSaved,
}: {
  regel: Regel;
  allow: string[];
  admin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [r, setR] = useState<Regel>(regel);
  const [worte, setWorte] = useState(regel.beschreibung ?? '');
  const [bobBusy, setBobBusy] = useState(false);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [probe, setProbe] = useState<Probelauf | null>(null);
  const [probeBusy, setProbeBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const brauchtAdmin = r.aktionen.some((a) => ADMIN_AKTIONEN.has(a.typ));

  const bob = async () => {
    if (!worte.trim()) return;
    setBobBusy(true);
    try {
      const j = await api.regelBob(worte);
      setR({ ...j.regel, id: r.id, aktiv: false, beschreibung: worte });
      setHinweis(j.hinweis);
      setProbe(null);
      toast('Bob hat einen Vorschlag gebaut – bitte prüfen.');
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBobBusy(false);
    }
  };
  const probelauf = async () => {
    setProbeBusy(true);
    try {
      setProbe(await api.regelProbelauf(r.bedingungen));
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setProbeBusy(false);
    }
  };
  const save = async () => {
    if (!r.name.trim()) return toast('Die Regel braucht einen Namen.');
    if (!r.bedingungen.alle.length && !r.bedingungen.eine.length)
      return toast('Mindestens eine Bedingung.');
    if (!r.aktionen.length) return toast('Mindestens eine Aktion.');
    setSaving(true);
    try {
      await api.regelSave({ ...r, beschreibung: worte || null });
      toast(
        r.id ? 'Regel gespeichert' : 'Regel angelegt (pausiert – nach dem Probelauf einschalten)',
      );
      onSaved();
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  const setBed = (gruppe: 'alle' | 'eine', i: number, b: Bedingung | null) =>
    setR({
      ...r,
      bedingungen: {
        ...r.bedingungen,
        [gruppe]: b
          ? r.bedingungen[gruppe].map((x, j) => (j === i ? b : x))
          : r.bedingungen[gruppe].filter((_, j) => j !== i),
      },
    });
  const setAkt = (i: number, a: Aktion | null) =>
    setR({
      ...r,
      aktionen: a
        ? r.aktionen.map((x, j) => (j === i ? a : x))
        : r.aktionen.filter((_, j) => j !== i),
    });

  return (
    <div className="rg-edit">
      <div className="rg-worte">
        <div className="s-subj">Regel in normalen Worten</div>
        <textarea
          rows={2}
          value={worte}
          onChange={(e) => setWorte(e.target.value)}
          placeholder="z. B. Wenn jemand aus dem Staff eine PDF-Rechnung schickt, an easybill übergeben, Empfang bestätigen und erledigen."
        />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn sm bob"
            disabled={bobBusy || !worte.trim()}
            onClick={() => void bob()}
          >
            {bobBusy ? 'Bob baut …' : 'Bob, bau die Regel'}
          </button>
          {hinweis && (
            <span className="muted" style={{ fontSize: 12 }}>
              {hinweis}
            </span>
          )}
        </div>
      </div>
      <label className="rg-field">
        <span>Name</span>
        <input
          value={r.name}
          onChange={(e) => setR({ ...r, name: e.target.value })}
          placeholder="Staff-Rechnungen an easybill"
        />
      </label>
      <div className="s-subj">Wenn … (alle müssen zutreffen)</div>
      {r.bedingungen.alle.map((b, i) => (
        <BedingungZeile key={i} b={b} onChange={(x) => setBed('alle', i, x)} />
      ))}
      <AddMenu
        label="+ Bedingung"
        items={Object.entries(BEDINGUNG_LABEL)}
        onPick={(art) =>
          setR({
            ...r,
            bedingungen: {
              ...r.bedingungen,
              alle: [...r.bedingungen.alle, neueBedingung(art as Bedingung['art'])],
            },
          })
        }
      />
      <div className="s-subj" style={{ marginTop: 8 }}>
        … oder mindestens eine davon <span className="muted">(optional)</span>
      </div>
      {r.bedingungen.eine.map((b, i) => (
        <BedingungZeile key={i} b={b} onChange={(x) => setBed('eine', i, x)} />
      ))}
      <AddMenu
        label="+ Oder-Bedingung"
        items={Object.entries(BEDINGUNG_LABEL)}
        onPick={(art) =>
          setR({
            ...r,
            bedingungen: {
              ...r.bedingungen,
              eine: [...r.bedingungen.eine, neueBedingung(art as Bedingung['art'])],
            },
          })
        }
      />
      <div className="s-subj" style={{ marginTop: 8 }}>
        Dann …
      </div>
      {r.aktionen.map((a, i) => (
        <AktionZeile key={i} a={a} allow={allow} onChange={(x) => setAkt(i, x)} />
      ))}
      <AddMenu
        label="+ Aktion"
        items={Object.entries(AKTION_LABEL).filter(
          ([t]) => admin || !ADMIN_AKTIONEN.has(t as Aktion['typ']),
        )}
        onPick={(typ) =>
          setR({ ...r, aktionen: [...r.aktionen, neueAktion(typ as Aktion['typ'], allow)] })
        }
      />
      {brauchtAdmin && !admin && (
        <div className="callout warn">
          Weiterleitung und easybill-Übergabe dürfen nur Admins anlegen.
        </div>
      )}
      <div className="rg-probe">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn sm"
            disabled={probeBusy || (!r.bedingungen.alle.length && !r.bedingungen.eine.length)}
            onClick={() => void probelauf()}
          >
            {probeBusy ? 'Probelauf läuft …' : 'Probelauf (letzte 30 Tage)'}
          </button>
          <span className="muted" style={{ fontSize: 12 }}>
            Zeigt, welche Mails gepasst hätten. Es wird nichts verschickt.
          </span>
        </div>
        {probe && (
          <div className="rg-probe-res">
            <div>
              <b>{probe.treffer.length}</b> Treffer von {probe.geprueft} geprüften Mails ·{' '}
              <b>{probe.knapp.length}</b> knapp nicht
            </div>
            {probe.hinweis && (
              <div className="muted" style={{ fontSize: 12 }}>
                {probe.hinweis}
              </div>
            )}
            {probe.treffer.slice(0, 20).map((t) => (
              <a
                key={t.id}
                className="rg-hit"
                href={t.threadId ? `/t/${t.threadId}` : '#'}
                target="_blank"
                rel="noreferrer"
              >
                <span className="pill ok">passt</span> {t.von} · {t.betreff}{' '}
                <span className="muted">{formatDate(t.datum)}</span>
              </a>
            ))}
            {probe.knapp.slice(0, 10).map((t) => (
              <a
                key={'k' + t.id}
                className="rg-hit"
                href={t.threadId ? `/t/${t.threadId}` : '#'}
                target="_blank"
                rel="noreferrer"
              >
                <span className="pill warn">knapp</span> {t.von} · {t.betreff}{' '}
                <span className="muted">fehlt: {t.fehlt}</span>
              </a>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button
          className="btn sm pri"
          disabled={saving || (brauchtAdmin && !admin)}
          onClick={() => void save()}
        >
          {r.id ? 'Speichern' : 'Anlegen'}
        </button>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={r.aktiv}
            onChange={(e) => setR({ ...r, aktiv: e.target.checked })}
          />{' '}
          aktiv
        </label>
        <button className="btn sm ghost" onClick={onClose}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

function AddMenu({
  label,
  items,
  onPick,
}: {
  label: string;
  items: Array<[string, string]>;
  onPick: (k: string) => void;
}) {
  return (
    <select
      className="rg-add"
      value=""
      onChange={(e) => {
        if (e.target.value) onPick(e.target.value);
      }}
    >
      <option value="">{label}</option>
      {items.map(([k, v]) => (
        <option key={k} value={k}>
          {v}
        </option>
      ))}
    </select>
  );
}

function BedingungZeile({
  b,
  onChange,
}: {
  b: Bedingung;
  onChange: (b: Bedingung | null) => void;
}) {
  const body = (() => {
    switch (b.art) {
      case 'absender_adresse':
      case 'absender_domain':
      case 'postfach':
      case 'betreff_enthaelt':
      case 'text_enthaelt':
        return (
          <input
            value={b.wert}
            onChange={(e) => onChange({ ...b, wert: e.target.value })}
            placeholder="…"
          />
        );
      case 'anhang':
        return (
          <select
            value={b.typ}
            onChange={(e) => onChange({ ...b, typ: e.target.value as 'vorhanden' | 'pdf' })}
          >
            <option value="vorhanden">vorhanden</option>
            <option value="pdf">ist eine PDF</option>
          </select>
        );
      case 'wochentag':
        return (
          <span className="rg-days">
            {WOCHENTAGE.map((w, i) => (
              <label key={w}>
                <input
                  type="checkbox"
                  checked={b.tage.includes(i)}
                  onChange={(e) =>
                    onChange({
                      ...b,
                      tage: e.target.checked
                        ? [...b.tage, i].sort()
                        : b.tage.filter((x) => x !== i),
                    })
                  }
                />{' '}
                {w}
              </label>
            ))}
          </span>
        );
      case 'uhrzeit':
        return (
          <>
            <input
              type="time"
              value={b.von}
              onChange={(e) => onChange({ ...b, von: e.target.value })}
            />{' '}
            –{' '}
            <input
              type="time"
              value={b.bis}
              onChange={(e) => onChange({ ...b, bis: e.target.value })}
            />
          </>
        );
      case 'bob_erkennt':
        return (
          <>
            <input
              value={b.text}
              onChange={(e) => onChange({ ...b, text: e.target.value })}
              placeholder="z. B. Inhalt ist eine Rechnung"
              style={{ flex: 1 }}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              ab
            </span>
            <input
              type="number"
              min={50}
              max={100}
              step={5}
              value={Math.round(b.minSicherheit * 100)}
              onChange={(e) => onChange({ ...b, minSicherheit: Number(e.target.value) / 100 })}
              style={{ width: 60 }}
            />{' '}
            %
          </>
        );
      default:
        return null;
    }
  })();
  return (
    <div className="rg-line">
      <span className="rg-lbl">{BEDINGUNG_LABEL[b.art]}</span>
      {body}
      <button className="btn sm ghost" onClick={() => onChange(null)} title="Entfernen">
        ✕
      </button>
    </div>
  );
}

function AktionZeile({
  a,
  allow,
  onChange,
}: {
  a: Aktion;
  allow: string[];
  onChange: (a: Aktion | null) => void;
}) {
  const body = (() => {
    switch (a.typ) {
      case 'easybill':
        return (
          <label>
            <input
              type="checkbox"
              checked={a.bestaetigung}
              onChange={(e) => onChange({ ...a, bestaetigung: e.target.checked })}
            />{' '}
            Empfang bestätigen
          </label>
        );
      case 'weiterleiten':
        return (
          <>
            <select value={a.an} onChange={(e) => onChange({ ...a, an: e.target.value })}>
              {allow.length === 0 && <option value="">(Freigabeliste ist leer)</option>}
              {allow.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
            <select
              value={a.modus}
              onChange={(e) =>
                onChange({ ...a, modus: e.target.value as 'weiterleitung' | 'umleitung' })
              }
            >
              <option value="weiterleitung">als Weiterleitung</option>
              <option value="umleitung">als Umleitung (Original-Absender)</option>
            </select>
          </>
        );
      case 'antworten':
        return (
          <>
            <input
              value={a.betreff ?? ''}
              onChange={(e) => onChange({ ...a, betreff: e.target.value || undefined })}
              placeholder="Betreff (optional)"
            />
            <textarea
              rows={3}
              value={a.text}
              onChange={(e) => onChange({ ...a, text: e.target.value })}
              placeholder="Text der Antwort"
              style={{ flex: 1 }}
            />
          </>
        );
      case 'zuweisen':
        return (
          <select
            value={a.an}
            onChange={(e) => onChange({ ...a, an: e.target.value as 'till' | 'lars' | 'maren' })}
          >
            {(['till', 'lars', 'maren'] as const).map((k) => (
              <option key={k} value={k}>
                {TEAM[k].name}
              </option>
            ))}
          </select>
        );
      case 'einsortieren':
        return (
          <select
            value={a.kategorie}
            onChange={(e) => onChange({ ...a, kategorie: e.target.value as 'info' | 'junk' })}
          >
            <option value="info">Info & System</option>
            <option value="junk">Junk</option>
          </select>
        );
      case 'spaeter':
        return (
          <select
            value={a.preset}
            onChange={(e) =>
              onChange({
                ...a,
                preset: e.target.value as 'today16' | 'tomorrow' | 'monday' | 'nextweek',
              })
            }
          >
            {Object.entries(PRESETS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        );
      case 'crm_notiz':
        return (
          <input
            value={a.text}
            onChange={(e) => onChange({ ...a, text: e.target.value })}
            placeholder="Text der Notiz"
            style={{ flex: 1 }}
          />
        );
      case 'crm_erinnerung':
        return (
          <>
            <input
              value={a.titel}
              onChange={(e) => onChange({ ...a, titel: e.target.value })}
              placeholder="Titel"
              style={{ flex: 1 }}
            />
            in{' '}
            <input
              type="number"
              min={0}
              max={365}
              value={a.tage}
              onChange={(e) => onChange({ ...a, tage: Number(e.target.value) })}
              style={{ width: 56 }}
            />{' '}
            Tagen
            <select
              value={a.fuer ?? ''}
              onChange={(e) =>
                onChange({
                  ...a,
                  fuer: (e.target.value || undefined) as 'till' | 'lars' | 'maren' | undefined,
                })
              }
            >
              <option value="">für mich</option>
              {(['till', 'lars', 'maren'] as const).map((k) => (
                <option key={k} value={k}>
                  {TEAM[k].name}
                </option>
              ))}
            </select>
          </>
        );
      case 'bob_entwurf':
        return (
          <input
            value={a.hinweis ?? ''}
            onChange={(e) => onChange({ ...a, hinweis: e.target.value || undefined })}
            placeholder="Hinweis für Bob, z. B. Handbuch-Kapitel"
            style={{ flex: 1 }}
          />
        );
      default:
        return null;
    }
  })();
  return (
    <div className="rg-line">
      <span className="rg-lbl">{AKTION_LABEL[a.typ]}</span>
      {body}
      <button className="btn sm ghost" onClick={() => onChange(null)} title="Entfernen">
        ✕
      </button>
    </div>
  );
}

function Protokoll() {
  const [rows, setRows] = useState<ProtokollEintrag[] | null>(null);
  const load = useCallback(
    () =>
      api
        .regelProtokoll()
        .then((j) => setRows(j.eintraege))
        .catch((e: Error) => toast(e.message)),
    [],
  );
  useEffect(() => void load(), [load]);
  if (!rows) return <div className="muted">Protokoll wird geladen …</div>;
  if (!rows.length) return <div className="muted">Noch keine Regel ausgeführt.</div>;
  return (
    <div>
      {rows.map((e) => (
        <div key={e.id} className="rg-row" style={{ alignItems: 'flex-start' }}>
          <div className="rg-main">
            <div className="rg-name">
              ⚙ {e.ruleName}{' '}
              <span
                className={`pill ${e.rueckgaengigGemacht ? 'mut' : e.ergebnis === 'ausgeführt' ? 'ok' : e.ergebnis === 'unsicher' ? 'warn' : 'warn'}`}
              >
                {e.rueckgaengigGemacht ? 'zurückgenommen' : e.ergebnis}
              </span>
            </div>
            <div className="rg-text">
              {e.threadId ? (
                <a href={`/t/${e.threadId}`} target="_blank" rel="noreferrer">
                  {e.betreff || '(kein Betreff)'}
                </a>
              ) : (
                e.betreff
              )}{' '}
              <span className="muted">· {e.von}</span>
            </div>
            <div className="muted" style={{ fontSize: 12 }}>
              {e.aktionen.map((a, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  {a.ok ? '✓' : '✗'} {a.typ}
                  {a.info ? ` (${a.info})` : ''}
                </span>
              ))}
              {e.begruendung && <div>{e.begruendung}</div>}
              {e.easybill && <div>easybill-Anhang #{e.easybill}</div>}
            </div>
            <div className="muted" style={{ fontSize: 11.5 }}>
              {formatDate(e.am)}
            </div>
          </div>
          {e.rueckgaengigMoeglich && (
            <button
              className="btn sm"
              onClick={() =>
                void api
                  .regelRueckgaengig(e.id)
                  .then(() => {
                    toast('Zurückgenommen');
                    void load();
                  })
                  .catch((err: Error) => toast(err.message))
              }
            >
              Rückgängig
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Freigabeliste({ data, reload }: { data: RegelnResponse; reload: () => Promise<void> }) {
  const [adr, setAdr] = useState('');
  const [label, setLabel] = useState('');
  const add = async () => {
    try {
      await api.freigabeAdd(adr.trim(), label.trim() || undefined);
      setAdr('');
      setLabel('');
      await reload();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  return (
    <div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
        Regeln dürfen nur an diese Adressen weiterleiten. Neue Ziele muss ein Admin freigeben.
        Eigene Postfächer sind ausgeschlossen (Schleifenschutz). Ein Eintrag mit „easybill“ im Namen
        dient als Rückfall, wenn der easybill-Upload scheitert.
      </div>
      {data.freigabeliste.map((f) => (
        <div key={f.id} className="rg-row">
          <div className="rg-main">
            <b>{f.address}</b> <span className="muted">{f.label}</span>
          </div>
          {data.admin && (
            <button
              className="btn sm ghost"
              onClick={() => void api.freigabeDelete(f.id).then(reload)}
              title="Entfernen"
            >
              ✕
            </button>
          )}
        </div>
      ))}
      {data.admin ? (
        <div className="rg-line" style={{ marginTop: 8 }}>
          <input
            value={adr}
            onChange={(e) => setAdr(e.target.value)}
            placeholder="adresse@beispiel.de"
            style={{ flex: 1 }}
          />
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Bezeichnung (z. B. easybill-Belegeingang)"
            style={{ flex: 1 }}
          />
          <button
            className="btn sm pri"
            disabled={!/^\S+@\S+\.\S+$/.test(adr)}
            onClick={() => void add()}
          >
            Freigeben
          </button>
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 12 }}>
          Nur Admins ändern die Freigabeliste.
        </div>
      )}
    </div>
  );
}
